import { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
    getMaintenanceStatus,
    verifyMaintenanceCode,
    MaintenanceStatus
} from "../../lib/api/maintenance";
import MaintenanceGatePage from "./MaintenanceGatePage";

interface MaintenanceGuardProps {
    children: ReactNode;
}

type GuardState = 'loading' | 'blocked' | 'allowed';

/**
 * MaintenanceGuard wraps the entire application and checks if maintenance mode is enabled.
 * 
 * If maintenance mode is enabled and the browser doesn't have access,
 * it shows the MaintenanceGatePage instead of the app.
 * 
 * Once the correct code is entered, access is granted via a cookie
 * and the app is shown normally.
 */
export const MaintenanceGuard = ({ children }: MaintenanceGuardProps) => {
    const { t } = useTranslation("common");
    const [state, setState] = useState<GuardState>('loading');
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        checkMaintenanceStatus();
    }, []);

    const checkMaintenanceStatus = async () => {
        try {
            const status: MaintenanceStatus = await getMaintenanceStatus();

            if (!status.maintenanceMode) {
                // Maintenance mode is disabled, allow access
                setState('allowed');
            } else if (status.hasAccess) {
                // Maintenance mode is enabled but user has access
                setState('allowed');
            } else {
                // Maintenance mode is enabled and user doesn't have access
                setState('blocked');
            }
        } catch (err) {
            // If we can't check status, assume no maintenance and allow access
            console.warn('Failed to check maintenance status, allowing access:', err);
            setState('allowed');
        }
    };

    const handleVerify = async (code: string) => {
        setError(undefined);
        try {
            await verifyMaintenanceCode(code);
            // Verification successful, the cookie is now set
            // Allow access
            setState('allowed');
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.message || t('maintenance.invalidCode');
            setError(errorMessage);
            throw err; // Re-throw so the form can handle it
        }
    };

    const handleAccessGranted = () => {
        setState('allowed');
    };

    // Loading state - show a minimal loading indicator
    if (state === 'loading') {
        return (
            <div className="min-h-screen bg-background-page flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                    <p className="text-neutral-500 text-sm">{t('maintenance.loading')}</p>
                </div>
            </div>
        );
    }

    // Blocked state - show the gate page
    if (state === 'blocked') {
        return (
            <MaintenanceGatePage
                onAccessGranted={handleAccessGranted}
                onVerify={handleVerify}
                error={error}
            />
        );
    }

    // Allowed state - render the app
    return <>{children}</>;
};

export default MaintenanceGuard;
